
import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Database } from '@/integrations/supabase/types';
import { Skeleton } from '@/components/ui/skeleton';

// Using a more flexible type definition
type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  tags?: { id: string; name: string }[];
};

interface BlogPostNavigationProps {
  previousPost: BlogPost | null;
  nextPost: BlogPost | null;
  loading?: boolean;
  isNavigating?: boolean;
  onNavigate?: () => void;
}

export function BlogPostNavigation({ 
  previousPost, 
  nextPost, 
  loading = false,
  isNavigating = false,
  onNavigate
}: BlogPostNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Add render tracking
  const renderCount = useRef(0);
  
  useEffect(() => {
    renderCount.current += 1;
    console.log(`[DEBUG] BlogPostNavigation RENDER #${renderCount.current}`);
    console.log(`[DEBUG] BlogPostNavigation props - prevPost: ${previousPost?.slug}, nextPost: ${nextPost?.slug}, loading: ${loading}, isNavigating: ${isNavigating}`);
  });

  if (loading && !previousPost && !nextPost) {
    return (
      <div className="flex justify-between items-center mt-8 border-t border-b py-4">
        <div><Skeleton className="h-10 w-32" /></div>
        <div><Skeleton className="h-10 w-32" /></div>
      </div>
    );
  }

  const handleNavigate = (slug: string | undefined) => {
    if (!slug) return;
    
    console.log(`[DEBUG] BlogPostNavigation - handleNavigate called with slug: ${slug}`);
    
    if (onNavigate) {
      console.log(`[DEBUG] BlogPostNavigation - Calling onNavigate callback`);
      onNavigate();
    }
    
    const newPath = `/blog/${slug}`;
    console.log(`[DEBUG] BlogPostNavigation - Current path: ${location.pathname}, new path: ${newPath}`);
    
    // Use proper React Router navigation
    navigate(newPath);
  };

  return (
    <div className="flex justify-between items-center mt-8 border-t border-b py-4">
      <div>
        {previousPost && previousPost.slug && (
          <Button 
            variant="ghost" 
            className="group" 
            disabled={isNavigating}
            onClick={() => handleNavigate(previousPost.slug)}
          >
            <div className="flex items-center">
              <ChevronLeft className={`h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1 ${isNavigating ? 'opacity-50' : ''}`} />
              <div className="text-left">
                <div className="text-xs text-muted-foreground">Previous</div>
                <div className={`font-medium line-clamp-1 ${isNavigating ? 'opacity-70' : ''}`}>
                  {previousPost.title}
                </div>
              </div>
            </div>
          </Button>
        )}
      </div>
      
      <div>
        {nextPost && nextPost.slug && (
          <Button 
            variant="ghost" 
            className="group"
            disabled={isNavigating}
            onClick={() => handleNavigate(nextPost.slug)}
          >
            <div className="flex items-center">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Next</div>
                <div className={`font-medium line-clamp-1 ${isNavigating ? 'opacity-70' : ''}`}>
                  {nextPost.title}
                </div>
              </div>
              <ChevronRight className={`h-4 w-4 ml-2 transition-transform group-hover:translate-x-1 ${isNavigating ? 'opacity-50' : ''}`} />
            </div>
          </Button>
        )}
      </div>
    </div>
  );
}
